import { CalendarDays, SearchX } from 'lucide-react';
import { Button, EmptyState, cn, type Column } from '@eombor/ui';
import { ResponsiveTable } from '../../../shared/ui/ResponsiveTable.js';
import { t } from '../../../shared/i18n/index.js';
import { unitText } from '../../../shared/i18n/labels.js';
import type { ItemFilter, SupplyItem } from '../model/supply-items.js';
import { ItemCheckbox, ItemPlacement, ItemStatus, itemRowClassName } from './ItemRow.js';
import { SupplyItemCards } from './SupplyItemCards.js';
import { TablePager } from './TablePager.js';

export interface ItemsTableProps {
  /** FAQAT joriy sahifadagi satrlar. */
  rows: SupplyItem[];
  selected: ReadonlySet<string>;
  onToggle: (id: string) => void;
  onPlace: (item: SupplyItem) => void;
  onUndo: (lineId: string) => void;
  /** Savatda turgan satrlar — bekor qilish tugmasi shularda chiziladi. */
  inBasket: ReadonlySet<string>;

  /**
   * «Qayerda turibdi» oynasi. Berilmasa «joylandi» soni oddiy matn
   * bo'lib qoladi — masalan nomenklaturasi tanilmagan satrda ko'rsatadigan
   * joy yo'q.
   */
  onShowLocation?: (item: SupplyItem) => void;

  canPlace: boolean;
  canSeeMoney: boolean;

  search: string;
  filter: ItemFilter;
  onClearSearch: () => void;

  page: number;
  pages: number;
  from: number;
  to: number;
  total: number;
  onPageChange: (page: number) => void;

  /**
   * Jadval toolbar bilan BITTA konteynerda turibdimi.
   *
   * Keng ekranda — ha: toolbar yuqori qirra, jadval esa uning ostidan
   * chegarasiz davom etadi. Telefonda jadval umuman chizilmaydi
   * (kartochkalar keladi) va konteyner ham bo'lmaydi.
   *
   * Bayroq `lg:` sinflari o'rniga ATAYLAB: `cn` — tailwind-merge emas,
   * ya'ni bir xil xossaga tegadigan ikkita utilitadan qaysi biri ustun
   * kelishi yozilish tartibiga bog'liq bo'lmaydi. Bayroq bilan har
   * holatda BITTA sinf to'plami chiziladi va to'qnashuv umuman
   * tug'ilmaydi.
   */
  attached: boolean;
}

/**
 * SATRLAR JADVALI.
 *
 * Bo'lim sarlavhalari («JOYLASHTIRILGANLAR» / «QOLGANLAR») OLIB
 * TASHLANDI va `groups` umuman berilmaydi — jadval bitta yaxlit
 * ro'yxat. Sabab aynan shu bo'linish edi: satr joylashtirilgach u
 * bir guruhdan ikkinchisiga ko'char, ro'yxat qayta tuzilar va
 * hujjatdagi tartib buzilardi.
 *
 * Endi holat qatorning O'ZIDA ko'rinadi (fon, chap chekkadagi chiziq,
 * o'ngdagi nishon), ro'yxat esa hech qachon qayta tartiblanmaydi.
 */
export const ItemsTable = ({
  rows,
  selected,
  onToggle,
  onPlace,
  onUndo,
  inBasket,
  onShowLocation,
  canPlace,
  canSeeMoney,
  search,
  filter,
  onClearSearch,
  page,
  pages,
  from,
  to,
  total,
  onPageChange,
  attached,
}: ItemsTableProps) => {
  const columns: Column<SupplyItem>[] = [
    ...(canPlace
      ? ([
          {
            key: 'pick',
            /* Tanlash ustuni yorliqsiz: katakchaning O'ZIDA satr kodi
               `aria-label` bo'lib turadi va sarlavha uni takrorlardi. */
            header: '',
            width: '40px',
            render: (row) => (
              <ItemCheckbox item={row} checked={selected.has(row.id)} onToggle={onToggle} />
            ),
          },
        ] as Column<SupplyItem>[])
      : []),
    {
      /*
       * TARTIB RAQAM — hujjatning O'Z satr raqami (`lineNo`), ekrandagi
       * o'rni emas. Qidiruv, chip va sahifalashdan keyin ham
       * o'zgarmaydi: skladchi «12-satr» deganda qog'ozdagi hisobotdagi
       * satrni nazarda tutadi.
       */
      key: 'lineNo',
      header: t('supply.colNo'),
      align: 'right',
      width: '50px',
      render: (row) => <span className="tabular-nums text-text-faint">{row.lineNo}</span>,
    },
    {
      key: 'code',
      header: t('supply.colCode'),
      mono: true,
      width: '11%',
      /* Tor ekranda kod kesilishi mumkin — to'lig'i `title` da. */
      render: (row) => <span title={row.code}>{row.code}</span>,
    },
    {
      key: 'name',
      header: t('supply.colName'),
      width: '24%',
      /* Bir qatorga KESILADI — qator balandligi qat'iy 56px va matn uni
         cho'zib yubormasligi kerak. To'lig'i `title` da qoladi. */
      render: (row) => (
        <span className="block w-full truncate" title={row.name}>
          {row.name}
        </span>
      ),
    },
    {
      key: 'unitRaw',
      header: t('supply.colUnit'),
      width: '6%',
      /*
       * BIRLIK — o'zbekcha nom, 1C ning ruscha xom yozuvi emas.
       *
       * `unitRaw` («шт», «шт (упаковка)») rasmiy qog'oz uchun saqlanadi
       * va bazadan o'chirilmaydi; ekranda esa tanilgan birlikning
       * lug'atdagi nomi chiqadi. Xom matn `title` da qoladi — kimdir
       * fayl bilan solishtirmoqchi bo'lsa, u yerda turibdi.
       */
      render: (row) => <span title={row.unitRaw}>{unitText(row.unit)}</span>,
    },
    {
      key: 'balanceQty',
      header: t('supply.colQty'),
      align: 'right',
      width: '8%',
      /* Sonlar ustun bo'ylab tekis tursin, lekin shrift oilasi
         o'zgarmasin: monospace faqat kodlar uchun. */
      render: (row) => <span className="tabular-nums">{row.balanceQty}</span>,
    },
    {
      key: 'toPlaceQty',
      header: t('supply.colToPlace'),
      align: 'right',
      /* Ilgari 8% edi va u yerga bitta raqam sig'ardi. Endi katakda uch
         qavat bor («7 qolgan», «3 / 10 joylandi», chiziq) va tor
         ustunda ular o'ralib ketardi. */
      width: '11%',
      render: (row) => (
        <ItemPlacement
          item={row}
          {...(onShowLocation === undefined || row.nomenclatureId === null
            ? {}
            : { onShowLocation: () => onShowLocation(row) })}
        />
      ),
    },
    {
      key: 'supplier',
      header: t('supply.colSupplier'),
      width: '18%',
      /*
       * Uzun nom («… MCHJ qo'shma korxonasi») JADVALDA bir qatorga
       * kesiladi, kartochkada esa to'liq o'qiladi. Shuning uchun kesish
       * `lg:` bilan: kartochka faqat 1024px dan TOR ekranda chiziladi
       * va u yerda bu sinflar umuman ishlamaydi.
       */
      render: (row) =>
        row.supplier === null ? (
          <span className="text-text-muted">{t('supply.supplierUnknown')}</span>
        ) : (
          /* Jadvalda bir qatorga kesiladi, kartochkada to'liq o'qiladi:
             kartochka faqat 1024px dan TOR ekranda chiziladi va `lg:`
             sinflari u yerda umuman ishlamaydi. Ikkala holatda ham
             to'liq nom `title` da qoladi. */
          <span className="block w-full lg:truncate" title={row.supplier}>
            {row.supplier}
          </span>
        ),
    },
    {
      key: 'arrivedAt',
      header: t('supply.colArrivedAt'),
      width: '13%',
      /*
        KIRIM SANASI — qarorga ta'sir qiladigan maydon: skladchi qaysi
        partiyani oldin joylashtirishni shunga qarab hal qiladi (eski
        kelgani oldin). Shuning uchun qiymat qalinroq va TO'Q rangda,
        yonida kalendar belgisi.
      */
      render: (row) =>
        row.arrivedAt === null ? (
          <span className="text-text-faint">—</span>
        ) : (
          /* Sana IKKI QATORGA tushmaydi: qator balandligi qat'iy va
             o'ralgan sana uni buzardi. */
          <span className="inline-flex items-center gap-1.5 whitespace-nowrap tabular-nums font-semibold text-text">
            <CalendarDays size={14} aria-hidden className="shrink-0 text-text-faint" />
            {row.arrivedAt}
          </span>
        ),
    },
    ...(canSeeMoney
      ? ([
          /* Hisob raqami — KOD, shuning uchun monospace qoladi. */
          { key: 'account', header: t('supply.colAccount'), mono: true, width: '7%' },
          {
            key: 'price',
            header: t('supply.colPrice'),
            align: 'right',
            width: '9%',
            render: (row) => (
              <span className="tabular-nums">
                {row.price === null ? '—' : row.price.toLocaleString('ru-RU')}
              </span>
            ),
          },
        ] as Column<SupplyItem>[])
      : []),
    ...(canPlace
      ? ([
          {
            key: 'actions',
            /* Oxirgi ustun endi YORLIQLI: u amal emas, HOLAT ko'rsatadi —
               «Joylashtirish» tugmasi ham, «Joylashtirildi» nishoni ham
               ayni bir savolga javob beradi. */
            header: t('supply.colRowStatus'),
            /* QAT'IY en, foiz emas: 1600px ekranda 12% atigi 125px edi —
               «Joylashtirildi» nishoni (91px) va bekor qilish tugmasi
               (32px) sig'masdi va nishon chapga surilib, kesilib qolardi.
               9.5rem = 128px mazmun + ikki yon 12px. */
            width: '9.5rem',
            align: 'right',
            render: (row) => (
              <ItemStatus
                item={row}
                inBasket={inBasket.has(row.id)}
                onPlace={onPlace}
                onUndo={onUndo}
              />
            ),
          },
        ] as Column<SupplyItem>[])
      : []),
  ];

  const hasPager = pages > 1;
  const filtered = search !== '' || filter !== 'all';

  const empty = filtered ? (
    <EmptyState
      icon={<SearchX size={22} aria-hidden />}
      title={t(search === '' ? 'supply.noneInFilter' : 'supply.noMatch')}
      action={
        search === '' ? undefined : (
          <Button variant="secondary" size="sm" onClick={onClearSearch}>
            {t('supply.clearSearch')}
          </Button>
        )
      }
    />
  ) : (
    <EmptyState title={t('supply.noLines')} />
  );

  const pager = hasPager ? (
    <div
      className={cn(
        'bg-surface',
        attached
          ? 'rounded-b-[var(--radius-lg)] border border-t-0 border-border'
          : 'mt-3 rounded-[var(--radius-lg)] border border-border',
      )}
    >
      <TablePager
        page={page}
        pages={pages}
        from={from}
        to={to}
        total={total}
        onChange={onPageChange}
      />
    </div>
  ) : null;

  /*
   * TOR EKRAN — supply'ning o'z kartochkalari (`SupplyItemCards`).
   * Umumiy kartochka bu yerda ishlatilmaydi: `rowClassName` dagi
   * `doc-row` uni qat'iy 56px ga qotirib qo'yardi (sababi o'sha faylda).
   */
  if (!attached) {
    return (
      <div className="mt-3 flex flex-col">
        {rows.length === 0 ? (
          <div className="rounded-2xl border border-border bg-surface p-4 shadow-[var(--shadow-1)]">
            {empty}
          </div>
        ) : (
          <SupplyItemCards
            caption={t('supply.linesCaption')}
            rows={rows}
            selected={selected}
            onToggle={onToggle}
            onPlace={onPlace}
            onUndo={onUndo}
            inBasket={inBasket}
            {...(onShowLocation === undefined ? {} : { onShowLocation })}
            canPlace={canPlace}
            canSeeMoney={canSeeMoney}
          />
        )}
        {pager}
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <ResponsiveTable
        caption={t('supply.linesCaption')}
        density="compact"
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        /*
         * Qator KALITI — satrning o'z `id` si, ro'yxatdagi o'rni emas.
         * Indeks ishlatilganda holat qo'shni qatorga siljib ketardi:
         * filtrdan keyin uchinchi qator boshqa satr bo'lib qolar, React
         * esa eski DOM tugunini qayta ishlatardi.
         */
        rowClassName={itemRowClassName}
        /*
         * O'z chegarasi O'CHIRILGAN va balandligi CHEKLANGAN.
         *
         * Chegara — toolbar bilan bitta konteyner bo'lishi uchun (pastda
         * qo'lda beriladi). Balandlik esa yopishqoq sarlavha uchun SHART:
         * `thead` dagi `top-0` eng yaqin aylantiruvchi qutiga nisbatan
         * o'lchanadi va o'sha quti aynan shu o'ram bo'lishi kerak.
         * Cheklanmasa o'ram mazmun bo'yicha cho'ziladi, hech qachon
         * aylanmaydi va sarlavha ham hech qachon yopishmaydi.
         */
        chrome={false}
        /*
         * `doc-table` — uslublar faylidagi qatlamsiz qoidalar:
         * aylantirish yo'q, `table-layout: fixed` va sarlavha toolbar
         * ostiga yopishadi. Ular Tailwind utilitalarini ATAYLAB
         * qoplaydi va `cn` sinf navbatiga tayanmaydi.
         */
        viewportClassName={cn(
          'doc-table border-x border-border',
          /* Sahifalagich bo'lmasa pastki qirra jadvalning O'ZIDA
             tugaydi — bo'sh chegarali tasma qolmasin. */
          hasPager ? undefined : 'rounded-b-[var(--radius-lg)] border-b',
        )}
        empty={empty}
      />

      {pager}
    </div>
  );
};
